import * as R from 'ramda'
import * as Nullable from './Nullable'
import {update} from './ObjectHelper'

export interface M<Filter, FilterType, FilterInput> {
    readonly inactiveFilters: FilterType[]
    readonly selectedFilter: FilterType | null
    readonly addFilterInput: FilterInput | null
    readonly activeFilters: Filter[]
    readonly popoverOpen: boolean
}

export function init<Filter, FilterType, FilterInput>(
    inactiveFilterTypes: (filters: Filter[]) => FilterType[],
    activeFilters: Filter[],
): M<Filter, FilterType, FilterInput> {
    return {
        inactiveFilters: inactiveFilterTypes(activeFilters),
        selectedFilter: null,
        addFilterInput: null,
        activeFilters,
        popoverOpen: false,
    }
}

export function setActiveFilters<Filter, FilterType extends string, FilterInput>(
    inactiveFilterTypesFromFilters: (filters: Filter[]) => FilterType[],
    activeFilters: Filter[],
    state: M<Filter, FilterType, FilterInput>,
): M<Filter, FilterType, FilterInput> {
    const newState: M<Filter, FilterType, FilterInput> =
        update(
            {
                activeFilters,
                inactiveFilters: inactiveFilterTypesFromFilters(activeFilters),
            },
            state,
        )
    return state.selectedFilter !== null && !R.contains(state.selectedFilter, newState.inactiveFilters)
        ? update({selectedFilter: null, addFilterInput: null}, newState)
        : newState
}

export function removeFilter<Filter, FilterType, FilterInput>(
    filterToType: (filter: Filter) => FilterType,
    sortFilterTypes: (filterTypes: FilterType[]) => FilterType[],
    filterType: FilterType,
    state: M<Filter, FilterType, FilterInput>
): M<Filter, FilterType, FilterInput> {
    return update(
        {
            activeFilters:
                R.filter(
                    filter =>
                        filterToType(filter) !== filterType,
                    state.activeFilters,
                ),
            inactiveFilters:
                sortFilterTypes(
                    R.append(
                        filterType,
                        state.inactiveFilters,
                    )
                ),
        },
        state,
    )
}

export function submit<Filter, FilterType, FilterInput>(
    filterFromInput: (filterInput: FilterInput) => Filter | null,
    filterToType: (filter: Filter) => FilterType,
    state: M<Filter, FilterType, FilterInput>
): M<Filter, FilterType, FilterInput> {
    const filter =
        Nullable.andThen(filterFromInput, state.addFilterInput)
    return filter !== null ?
        resetFilterInput(
            update(
                {
                    inactiveFilters:
                        R.filter(
                            filterType => filterType !== filterToType(filter),
                            state.inactiveFilters,
                        ),
                    activeFilters: R.append(filter, state.activeFilters),
                },
                state,
            )
        ) :
        state
}

export function clearFilters<Filter, FilterType, FilterInput>(
    allFilterTypes: FilterType[],
    state: M<Filter, FilterType, FilterInput>
): M<Filter, FilterType, FilterInput> {
    return resetFilterInput(
        update(
            {activeFilters: [], inactiveFilters: allFilterTypes},
            state,
        ),
    )
}

function resetFilterInput<Filter, FilterType, FilterInput>(
    state: M<Filter, FilterType, FilterInput>
): M<Filter, FilterType, FilterInput> {
    return update({addFilterInput: null, selectedFilter: null}, state)
}

export function reduceFilterInputChanged<Filter, FilterType, FilterInput, FilterInputAction>(
    actionReducer: (
        action: FilterInputAction,
        filterInput: FilterInput
    ) => FilterInput,
    action: FilterInputAction,
    state: M<Filter, FilterType, FilterInput>,
): M<Filter, FilterType, FilterInput> {
    const {addFilterInput} = state
    if (addFilterInput !== null) {
        const newAddFilterInput = actionReducer(action, addFilterInput)
        return update({addFilterInput: newAddFilterInput}, state)
    } else {
        return state
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Data/FilteringState.ts